/**
 * Theme music — the interim single-track cut of TRD-04 R-04-29.
 *
 * The song (150 BPM, 4/4) opens with a quiet 8-bar intro and then runs a 40-bar body
 * at full energy; measured by RMS-profiling the decoded track bar by bar, the body
 * spans EXACTLY 40 bars on the grid. So the intro plays once and the body loops
 * seamlessly via the Web Audio loop region — no gap, no drift, no re-trigger, which a
 * media-element `loop` could never do with a slow intro in front.
 *
 * This module owns playback only. WHEN to start is the app's problem (browsers demand
 * a user gesture), and every failure degrades to silence — the game must be fully
 * playable with audio missing or blocked (R-04-35's spirit).
 *
 * The adaptive stem system (per-layer mutes for shop/build/combat/boss) replaces this
 * when the per-stem exports arrive; the loop math here carries over unchanged.
 */

export const THEME_URL = '/assets/audio/theme.mp3';

/** Prism's track. Its own song, by the same composer — see `setBoss`. */
export const BOSS_URL = '/assets/audio/boss.mp3';

/** The menu's own song. Plays on every screen that is not a run. */
export const MENU_URL = '/assets/audio/menu.mp3';

/** Crossfade length between the theme and the boss track, in seconds. */
export const BOSS_CROSSFADE_SECONDS = 1.2;

/** Menu ⇄ run is a bigger scene change than theme ⇄ boss, so it takes longer. */
export const MENU_CROSSFADE_SECONDS = 1.6;

/**
 * The paused mix: the song carries on in the next room.
 *
 * A low-pass at this corner is what does the work — walls eat treble long before they
 * eat bass, so dropping everything above a few hundred hertz reads as "through a wall"
 * rather than as "turned down". The reverb is the room you are now hearing it in, and it
 * is deliberately a small pour: enough to smear the transients, not enough to sound like
 * a cathedral.
 */
export const MUFFLE_HZ = 420;
export const MUFFLE_FADE_SECONDS = 0.45;
export const MUFFLE_REVERB_SECONDS = 1.4;
/** Wet level while muffled. Past ~0.4 the melody stops being findable. */
export const MUFFLE_REVERB_WET = 0.32;

/** 150 BPM four-four: one bar is 1.6 s. From the composer's project (and the MIDI). */
export const BAR_SECONDS = (60 / 150) * 4;

/** First onset sits 30 ms in (encoder delay) — the bar grid hangs off this. */
export const GRID_OFFSET_SECONDS = 0.03;

/** Eight quiet intro bars, then the body. */
export const INTRO_BARS = 8;
export const BODY_BARS = 40;

/** The loop region: [intro end, intro end + body]. Pure, so the math is testable. */
export function themeLoopPoints(): { loopStart: number; loopEnd: number } {
  const loopStart = GRID_OFFSET_SECONDS + INTRO_BARS * BAR_SECONDS;
  return { loopStart, loopEnd: loopStart + BODY_BARS * BAR_SECONDS };
}

/**
 * An impulse response for the paused reverb, generated rather than shipped.
 *
 * Noise under an exponential decay is the standard cheap room, and it costs nothing to
 * make at boot — a real IR would be another asset to fetch, another thing to 404, for an
 * effect the player hears through a low-pass anyway.
 */
function makeRoomImpulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      // Decay steeply enough that the tail is gone before the next bar arrives.
      const decay = (1 - i / length) ** 2.6;
      data[i] = (Math.random() * 2 - 1) * decay;
    }
  }
  return impulse;
}

/** One gain ramp, cancelling whatever was already scheduled on it. */
function ramp(ctx: AudioContext, node: GainNode, target: number, seconds: number): void {
  const now = ctx.currentTime;
  node.gain.cancelScheduledValues(now);
  node.gain.setValueAtTime(node.gain.value, now);
  if (seconds <= 0) node.gain.setValueAtTime(target, now);
  else node.gain.linearRampToValueAtTime(target, now + seconds);
}

export class MusicPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  /** Everything the songs feed, before the dry/muffled split. */
  private bus: GainNode | null = null;
  /** Per-track gains under `bus`, crossfaded against each other. */
  private themeGain: GainNode | null = null;
  private bossGain: GainNode | null = null;
  private menuGain: GainNode | null = null;
  /** The two paths out of `bus`: straight through, or through the wall. */
  private dryGain: GainNode | null = null;
  private muffledGain: GainNode | null = null;
  private reverbGain: GainNode | null = null;
  private starting = false;
  private started = false;
  /** master × music, applied with a short ramp so slider drags do not zipper. */
  private volume = 0;
  /** True while the boss track owns the mix. */
  private bossActive = false;
  /** True while a menu screen owns the mix — outranks the boss, since a boss can be
   *  alive in a world the player has just walked out of. */
  private menuActive = true;
  /** True while the run is paused and the music is playing next door. */
  private muffled = false;

  /** Call from a user gesture — autoplay policy blocks an unprompted AudioContext. */
  start(): void {
    if (this.started || this.starting) return;
    this.starting = true;
    void this.boot().catch(() => {
      // Silence is a supported state: decode failure, missing file, blocked context —
      // the game plays on without music rather than surfacing an error.
      this.starting = false;
    });
  }

  private async boot(): Promise<void> {
    const ctx = new AudioContext();
    if (ctx.state === 'suspended') await ctx.resume();

    const res = await fetch(THEME_URL);
    if (!res.ok) throw new Error(`theme fetch ${res.status}`);
    const buffer = await ctx.decodeAudioData(await res.arrayBuffer());

    const gain = ctx.createGain();
    gain.gain.value = this.volume;
    gain.connect(ctx.destination);

    /*
     * bus ─┬─ dry ───────────────────────── gain ─ destination
     *      └─ lowpass ─┬─ muffled ───────── gain
     *                  └─ convolver ─ wet ─ gain
     *
     * Two permanent paths rather than a filter that gets patched in and out: swapping
     * nodes mid-playback clicks, and crossfading two gains does not. Unpaused the dry
     * path is the only one open, so the filter costs nothing audible.
     */
    const bus = ctx.createGain();

    const dryGain = ctx.createGain();
    dryGain.gain.value = 1;
    bus.connect(dryGain).connect(gain);

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = MUFFLE_HZ;
    lowpass.Q.value = 0.7;
    bus.connect(lowpass);

    const muffledGain = ctx.createGain();
    muffledGain.gain.value = 0;
    lowpass.connect(muffledGain).connect(gain);

    const convolver = ctx.createConvolver();
    convolver.buffer = makeRoomImpulse(ctx, MUFFLE_REVERB_SECONDS);
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0;
    lowpass.connect(convolver).connect(reverbGain).connect(gain);

    // Every track runs from the moment audio starts, and a crossfade only moves their
    // gains. Starting one on demand would mean decoding mid-fight — a stall exactly when
    // the frame budget is tightest — and would drop it in at a random bar.
    const themeGain = ctx.createGain();
    themeGain.gain.value = 0;
    themeGain.connect(bus);

    const bossGain = ctx.createGain();
    bossGain.gain.value = 0;
    bossGain.connect(bus);

    // The menu is the first thing anyone hears, so its track opens at full.
    const menuGain = ctx.createGain();
    menuGain.gain.value = this.menuActive ? 1 : 0;
    menuGain.connect(bus);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const { loopStart, loopEnd } = themeLoopPoints();
    source.loop = true;
    source.loopStart = loopStart;
    // Clamp defensively: a re-exported file with a different length must not push the
    // loop end past the buffer, which would silently disable looping in some browsers.
    source.loopEnd = Math.min(loopEnd, buffer.duration);
    source.connect(themeGain);
    source.start(0);

    this.ctx = ctx;
    this.gain = gain;
    this.bus = bus;
    this.themeGain = themeGain;
    this.bossGain = bossGain;
    this.menuGain = menuGain;
    this.dryGain = dryGain;
    this.muffledGain = muffledGain;
    this.reverbGain = reverbGain;
    this.started = true;
    // Whatever the app asked for while the context was still booting.
    this.applyMix(0);
    this.applyMuffle(0);

    // Both extra tracks are nice-to-haves: if one fails to load its gain simply never
    // has anything behind it, and the game plays on with what did load.
    void this.loadLoop(ctx, BOSS_URL, bossGain).catch(() => {});
    void this.loadLoop(ctx, MENU_URL, menuGain).catch(() => {});
  }

  /**
   * The three track gains, derived from the two flags rather than set independently.
   *
   * Menu outranks boss on purpose: Prism can still be alive in a world the player has
   * just walked out of, and the menu should not open over a boss track.
   */
  private applyMix(seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.themeGain || !this.bossGain || !this.menuGain) return;
    const menu = this.menuActive ? 1 : 0;
    const boss = !this.menuActive && this.bossActive ? 1 : 0;
    const theme = !this.menuActive && !this.bossActive ? 1 : 0;
    for (const [node, target] of [
      [this.menuGain, menu],
      [this.bossGain, boss],
      [this.themeGain, theme],
    ] as const) {
      ramp(ctx, node, target, seconds);
    }
  }

  private applyMuffle(seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.dryGain || !this.muffledGain || !this.reverbGain) return;
    ramp(ctx, this.dryGain, this.muffled ? 0 : 1, seconds);
    ramp(ctx, this.muffledGain, this.muffled ? 1 : 0, seconds);
    ramp(ctx, this.reverbGain, this.muffled ? MUFFLE_REVERB_WET : 0, seconds);
  }

  /** Fetch, decode and start a whole-file loop into `destination`. */
  private async loadLoop(ctx: AudioContext, url: string, destination: GainNode): Promise<void> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
    const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // Whole-file loop: no measured loop region for this one yet, and a boss fight rarely
    // outlives a single pass anyway.
    source.loop = true;
    source.connect(destination);
    source.start(0);
  }

  /**
   * Hand the mix to Prism's track, or take it back.
   *
   * Idempotent — the app calls it every frame the boss is alive, and only a change of
   * state schedules a ramp.
   */
  setBoss(active: boolean): void {
    if (active === this.bossActive) return;
    this.bossActive = active;
    this.applyMix(BOSS_CROSSFADE_SECONDS);
  }

  /**
   * Hand the mix to the menu track, or back to the run.
   *
   * Idempotent like `setBoss`, and safe before `start()` — the flag is remembered and
   * applied when the context finally boots, which matters because the menu is on screen
   * before the gesture that is allowed to create one.
   */
  setMenu(active: boolean): void {
    if (active === this.menuActive) return;
    this.menuActive = active;
    this.applyMix(MENU_CROSSFADE_SECONDS);
  }

  /**
   * Play the music through the wall, for pause.
   *
   * Not a volume drop: the low-pass and the small room are what say "still playing, just
   * not in here". Dropping the level instead would say "turned down", which is a
   * different thing and a worse one — the player loses the track rather than stepping
   * away from it.
   */
  setMuffled(active: boolean): void {
    if (active === this.muffled) return;
    this.muffled = active;
    this.applyMuffle(MUFFLE_FADE_SECONDS);
  }

  get isMuffled(): boolean {
    return this.muffled;
  }

  get isMenuPlaying(): boolean {
    return this.menuActive;
  }

  get isBossPlaying(): boolean {
    return this.bossActive;
  }

  /** Combined master × music volume from settings. Safe to call before start. */
  setVolume(volume: number): void {
    this.volume = volume;
    if (this.ctx && this.gain) {
      this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.gain.gain.linearRampToValueAtTime(volume, this.ctx.currentTime + 0.08);
    }
  }

  get isPlaying(): boolean {
    return this.started;
  }
}
